import { useMemo } from "react";
import { Button, Popover, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { INVOICE_STATUS_COLORS } from "../../../config/MilestoneInvoice.constants";
import {
  formatDisplayDate,
  resolveInvoiceStatusOption,
  toFieldglassStatus,
  toUiInvoiceStatus,
} from "../../../utils/milestoneDataMapper";
import { formatAmountWithCurrencyType, formatTrackerCurrency, getRowPopoverKey, invoiceStatusConfig } from "../tracker.utils";


const { Text } = Typography;



/** Builds the Invoices table column definitions (memoized on the callbacks/open popover key). */
export function useInvoiceColumns({
  popoverOpenKey,
  onPopoverOpenChange,
  onViewDetails,
  onUpdateStatusClick,
}) {
  return useMemo(
    () => [
      {
        title: "Invoice ID",
        dataIndex: "invoiceId",
        key: "invoiceId",
        align: "left",
        width: "13%",
        render: (v) => <span style={{ whiteSpace: "nowrap", fontSize: 11 }}>{v || "-"}</span>,
      },
      {
        title: "Project ID",
        dataIndex: "projectId",
        key: "projectId",
        width: "16%",
        align: "left",
        render: (v) => (
          <Tag color="blue" style={{ fontFamily: "monospace", fontSize: 10 }}>
            {v || "-"}
          </Tag>
        ),
      },
      {
        title: "Account Name",
        dataIndex: "accountName",
        key: "accountName",
        align: "left",
        width: "20%",
        render: (v) => (
          <Tooltip title={v}>
            <Text style={{ fontSize: 13, fontWeight: 500 }} ellipsis>
              {v || "-"}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Revenue Month",
        dataIndex: "revenueMonth",
        key: "revenueMonth",
        align: "left",
        width: "15%",
        render: (v) => v,
      },
      {
        title: "Date of Invoice Submitted",
        dataIndex: "invoiceSubmittedDate",
        key: "invoiceSubmittedDate",
        align: "left",
        width: "16%",
        render: (v) => formatDisplayDate(v),
      },
      {
        title: "Amount",
        dataIndex: "grossInvoiceAmount",
        key: "grossInvoiceAmount",
        align: "left",
        width: "13%",
        render: (v) => formatTrackerCurrency(v ?? 0),
      },
      {
        title: "Status",
        dataIndex: "approvalStatusInFieldglass",
        key: "approvalStatusInFieldglass",
        width: "27%",
        align: "left",
        render: (_, record) => {
          const statusLabel = toFieldglassStatus(record.approvalStatusInFieldglass, record.invoicingStatus);
          const textColor = INVOICE_STATUS_COLORS[statusLabel] || "inherit";

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ color: textColor, fontWeight: 600 }}>{statusLabel}</span>
              {record.reason ? (
                <div style={{ color: "#595959", fontSize: 12, whiteSpace: "pre-wrap" }}>{record.reason}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        title: "",
        key: "actions",
        width: "5%",
        align: "left",
        render: (_, r, index) => {
          const popoverKey = getRowPopoverKey(r, index);
          const resolvedStatus = resolveInvoiceStatusOption(r);

          return (
            <Popover
              content={
                <div style={{ width: 380 }}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                      Invoice Details
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Invoice Id:</span>{" "}
                      <Tag color="geekblue">{r.invoiceId || "-"}</Tag>
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c" }}>Project ID:</span>{" "}
                      <Tag color="blue">{r.projectId || "-"}</Tag>
                    </Text>
                    <Text style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                      <span style={{ color: "#8c8c8c", fontWeight: 600 }}>Amount:</span>{" "}
                      <span style={{ color: "#1677ff", fontWeight: 600 }}>
                        {formatTrackerCurrency(r.grossInvoiceAmount ?? 0)}
                      </span>
                    </Text>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                      Current Invoice Status
                    </Text>
                    <Tag
                      color={
                        INVOICE_STATUS_COLORS[resolvedStatus || ""] ||
                        invoiceStatusConfig[
                          toUiInvoiceStatus(r.approvalStatusInFieldglass, r.invoicingStatus, Boolean(r.revenueMonth))
                        ]?.color
                      }
                      style={{ fontSize: 12 }}
                    >
                      {resolvedStatus || "-"}
                    </Tag>
                  </div>

                  {r.reason && (
                    <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                        Notes
                      </Text>
                      <Text style={{ fontSize: 12, color: "#595959" }}>{r.reason}</Text>
                    </div>
                  )}

                  <Button
                    type="primary"
                    block
                    onClick={() => {
                      onPopoverOpenChange(null);
                      onUpdateStatusClick(r);
                    }}
                  >
                    Update Invoice Status
                  </Button>
                </div>
              }
              title="Invoice Information"
              trigger="click"
              open={popoverOpenKey === popoverKey}
              onOpenChange={(open) => onPopoverOpenChange(open ? popoverKey : null)}
              placement="topLeft"
              overlayClassName="trackon-root"
              overlayStyle={{ maxWidth: 420 }}
            >
              <Button icon={<InfoCircleOutlined />} size="small" type="text" onClick={() => onViewDetails(r)} />
            </Popover>
          );
        },
      },
    ],
    [popoverOpenKey, onPopoverOpenChange, onViewDetails, onUpdateStatusClick],
  );
}
